/**
 * PeerConnectionManager - Core P2P WebRTC connection management
 * Extracted from realP2PDemo_Stage2.html with V70 terminology compliance
 * Professional implementation for useSelft Standalone
 */

import { Peer, DataConnection } from 'peerjs'
import { PeerConnection, SelfTA, P2PEvent, EventHandler } from '../types'
import { AliasRegistryService } from './AliasRegistryService'
import { MessageService } from './MessageService'
import { MediaService } from './MediaService'
import { FileTransferService } from './FileTransferService'
import { toastManager, TOAST_GROUPS } from '../utils/toastManager'

export interface PeerConnectionConfig {
  host?: string
  port?: number
  path?: string
  secure?: boolean
  iceServers?: RTCIceServer[]
}

export interface ConnectionEventHandlers {
  onConnection: EventHandler<PeerConnection>
  onDisconnection: EventHandler<{ peerId: string; reason: string }>
  onData: EventHandler<{ peerId: string; data: unknown }>
  onError: EventHandler<{ peerId: string; error: Error }>
}

export class PeerConnectionManager {
  private peer: Peer | null = null
  private connections = new Map<string, { connection: DataConnection; info: PeerConnection }>()
  private localSelfta: SelfTA | null = null
  private initialized = false
  private eventHandlers = new Map<string, EventHandler[]>()
  private aliasRegistry: AliasRegistryService
  private messageService: MessageService
  private mediaService: MediaService
  private fileTransferService: FileTransferService
  private connectionTimeout = 15000 // 15 seconds (increased for mobile networks)
  private reconnectAttempts = new Map<string, number>()
  private maxReconnectAttempts = 3
  private networkStabilizationTime = 8000 // 8 seconds to wait for network stabilization (increased for mobile)
  private isNetworkStabilized = false

  constructor(
    private config: PeerConnectionConfig = {},
    private handlers?: Partial<ConnectionEventHandlers>
  ) {
    this.aliasRegistry = new AliasRegistryService()
    this.messageService = new MessageService('', '')
    // MOBILE FIX: Pass alias registry to MediaService for enhanced call name resolution
    this.mediaService = new MediaService('', '', null, this.aliasRegistry) // Will be updated when peer is created
    this.fileTransferService = new FileTransferService('', '')
    this.setupDefaultConfig()
    this.registerEventHandlers()
  }

  private setupDefaultConfig(): void {
    // Enhanced ICE server configuration for better mobile-desktop compatibility
    const enhancedIceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' }, // Additional Google STUN servers
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' },
      // Mozilla STUN servers for better compatibility
      { urls: 'stun:stun.services.mozilla.com' },
      // Additional reliable STUN servers for mobile networks
      { urls: 'stun:stun.voip.blackberry.com:3478' }
    ]

    this.config = {
      host: '0.peerjs.com',
      port: 443,
      path: '/',
      secure: true,
      iceServers: enhancedIceServers,
      ...this.config
    }
  }

  private registerEventHandlers(): void {
    if (this.handlers?.onConnection) {
      this.on('connection', this.handlers.onConnection)
    }
    if (this.handlers?.onDisconnection) {
      this.on('disconnection', this.handlers.onDisconnection)
    }
    if (this.handlers?.onData) {
      this.on('data', this.handlers.onData)
    }
    if (this.handlers?.onError) {
      this.on('error', this.handlers.onError)
    }
  }

  /**
   * Initialize P2P connection with SelfTA identity
   * CRITICAL FIX: Use alias as peer ID for direct connections based on help document
   */
  async initializeP2P(selfta: SelfTA, onPeerIdAssigned?: (peerId: string) => void): Promise<boolean> {
    try {
      if (this.initialized) {
        console.warn('PeerConnectionManager already initialized')
        return true
      }

      this.localSelfta = selfta

      // CRITICAL FIX: Use alias as peer ID for direct alias-based connections
      // Based on help-peer-alias-handshake.md: peer = new Peer("alice-2024", config)
      // This creates persistent, human-readable peer IDs that enable direct connections
      const customPeerId = selfta.alias && selfta.alias.trim() ?
        selfta.alias.trim() :
        undefined // Use undefined for random ID fallback

      // Create PeerJS instance with professional configuration
      const peerConfig: any = {}
      if (this.config.host) peerConfig.host = this.config.host
      if (this.config.port) peerConfig.port = this.config.port
      if (this.config.path) peerConfig.path = this.config.path
      if (this.config.secure !== undefined) peerConfig.secure = this.config.secure
      if (this.config.iceServers) {
        peerConfig.config = {
          iceServers: this.config.iceServers
        }
      }

      // Initialize with alias as peer ID (if provided)
      this.peer = customPeerId ? new Peer(customPeerId, peerConfig) : new Peer(peerConfig)

      return new Promise((resolve, reject) => {
        if (!this.peer) {
          reject(new Error('Failed to create peer instance'))
          return
        }

        this.peer.on('open', async (peerId) => {
          this.localSelfta = { ...this.localSelfta!, id: peerId }
          this.initialized = true

          // P2P Network initialized successfully
          if (peerId === selfta.alias) {
            console.log(`🎯 Alias-based peer ID active: '${peerId}' - direct connections enabled`)
            toastManager.success(`Connected as '${peerId}'`, TOAST_GROUPS.NETWORK, {
              duration: 3000,
              icon: '✅'
            })
          } else {
            console.log(`🔄 Random peer ID assigned: '${peerId.slice(0, 8)}...' - discovery will be needed`)
            toastManager.success(`Connected to P2P network`, TOAST_GROUPS.NETWORK, {
              duration: 3000,
              icon: '✅'
            })
          }

          // Register our alias in the local registry
          this.aliasRegistry.registerLocalAlias(selfta.alias, peerId)

          // Update services with peer information
          this.messageService.updateLocalPeer(peerId, selfta.alias)
          // MOBILE FIX: Pass alias registry to MediaService for enhanced mobile call resolution
          this.mediaService.updateLocalPeer(peerId, selfta.alias, this.peer, this.aliasRegistry)
          this.fileTransferService.updateLocalPeer(peerId, selfta.alias)

          // Inject connection finder into FileTransferService
          this.fileTransferService.setConnectionFinder((targetPeerId: string) => {
            const connectionData = this.connections.get(targetPeerId)
            return connectionData?.connection.open ? connectionData.connection : null
          })

          // Wait for network stabilization before marking as ready
          console.log(`⏳ Waiting for network stabilization (${this.networkStabilizationTime}ms)...`)
          toastManager.info(`Starting network...`, TOAST_GROUPS.NETWORK, {
            duration: this.networkStabilizationTime,
            icon: '⚡',
            style: {
              background: '#f3f4f6',
              color: '#374151'
            }
          })

          setTimeout(() => {
            this.isNetworkStabilized = true
            console.log(`🌐 P2P Network initialization completed`)
            toastManager.success(`Network ready`, TOAST_GROUPS.NETWORK, {
              duration: 3000,
              icon: '✅'
            })
          }, this.networkStabilizationTime)

          // Call the callback if provided (for state management)
          if (onPeerIdAssigned) {
            onPeerIdAssigned(peerId)
          }

          resolve(true)
        })

        this.peer.on('connection', (conn) => {
          this.handleIncomingConnection(conn).catch(error => {
            console.error('Error handling incoming connection:', error)
          })
        })

        // Handle incoming media calls
        this.peer.on('call', (mediaCall) => {
          console.log(`📞 Incoming media call from ${mediaCall.peer}`)
          console.log(`🚨 DEBUG: MediaCall metadata:`, mediaCall.metadata)
          console.log(`🚨 DEBUG: MediaCall object keys:`, Object.keys(mediaCall))
          this.mediaService.handleIncomingMediaCall(mediaCall)
        })

        this.peer.on('error', (error) => {
          console.error('PeerJS error:', error)
          this.emit('error', { peerId: '', error })
          reject(error)
        })

        this.peer.on('disconnected', () => {
          console.warn('Peer disconnected from signaling server')
          this.attemptReconnectToSignaling()
        })

        // Set timeout for initialization
        setTimeout(() => {
          if (!this.initialized) {
            reject(new Error('P2P initialization timeout'))
          }
        }, 15000)
      })
    } catch (error) {
      console.error('Failed to initialize P2P:', error)
      throw error
    }
  }

  /**
   * Connect to peer by their Peer ID - enhanced for alias-based system
   */
  async connectToPeer(targetPeerId: string): Promise<boolean> {
    if (!this.initialized || !this.peer || !this.localSelfta) {
      throw new Error('PeerConnectionManager not initialized')
    }

    // SECURITY: Input validation to prevent XSS/injection attacks
    if (!targetPeerId || typeof targetPeerId !== 'string') {
      throw new Error('Invalid peer ID: must be a non-empty string')
    }

    // Sanitize and validate peer ID format
    const sanitizedPeerId = targetPeerId.trim()
    if (sanitizedPeerId.length === 0) {
      throw new Error('Invalid peer ID: cannot be empty')
    }

    // Validate peer ID contains only safe characters (alphanumeric, hyphens, underscores)
    if (!/^[a-zA-Z0-9_-]+$/.test(sanitizedPeerId)) {
      throw new Error('Invalid peer ID: contains unsafe characters')
    }

    // Length validation to prevent extremely long inputs
    if (sanitizedPeerId.length > 200) {
      throw new Error('Invalid peer ID: too long (max 200 characters)')
    }

    if (sanitizedPeerId === this.localSelfta.id) {
      throw new Error('Cannot connect to self')
    }

    if (this.connections.has(sanitizedPeerId)) {
      console.log(`Already connected to '${sanitizedPeerId}'`)
      return true
    }

    try {
      // Enhanced target identification - try alias first, then format peer ID
      const targetAlias = this.aliasRegistry.getAlias(sanitizedPeerId) ||
        (sanitizedPeerId.length > 15 ? `Peer_${sanitizedPeerId.slice(-8)}` : sanitizedPeerId)

      console.log(`🔄 Connecting to '${targetAlias}' (ID: ${sanitizedPeerId.slice(0, 8)}...)...`)

      // MOBILE FIX: Enhanced connection configuration for mobile-desktop reliability
      const isMobileUserAgent = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

      // MOBILE FIX: Validate connection readiness before attempting
      if (!this.validateConnectionReadiness(isMobileUserAgent)) {
        throw new Error('Network not ready for new connections')
      }

      // MOBILE FIX: Progressive metadata to prevent message size issues
      const baseMetadata = {
        sourceAlias: this.localSelfta.alias,
        connectionMethod: this.isUsingAliasAsPeerId() ? 'direct-alias' : 'legacy-id',
        timestamp: Date.now(),
        isMobile: isMobileUserAgent
      }

      // MOBILE FIX: Additional metadata only for desktop connections or when explicitly needed
      const enhancedMetadata = isMobileUserAgent ? baseMetadata : {
        ...baseMetadata,
        clientVersion: '1.0.0',
        capabilities: ['messages', 'files', 'voice', 'video']
      }

      const conn = this.peer.connect(sanitizedPeerId, {
        reliable: true,
        serialization: 'json',
        metadata: enhancedMetadata
      })

      return this.establishConnection(conn, sanitizedPeerId)
    } catch (error) {
      console.error(`Failed to connect to '${sanitizedPeerId}':`, error)
      const targetAlias = this.aliasRegistry.getAlias(sanitizedPeerId) || sanitizedPeerId
      const errorMsg = `Failed to connect to '${targetAlias}' - ${error}`
      console.error(`❌ ${errorMsg}`)
      toastManager.error(`Connection failed`, TOAST_GROUPS.CONNECTION, {
        duration: 4000,
        icon: '❌'
      })
      return false
    }
  }

  /**
   * Wait for network stabilization
   */
  private async waitForNetworkStabilization(timeoutMs: number = 10000): Promise<boolean> {
    const startTime = Date.now()
    const pollInterval = 500 // Check every 500ms

    return new Promise((resolve) => {
      const checkStabilization = () => {
        if (this.isNetworkStabilized) {
          resolve(true)
          return
        }

        if (Date.now() - startTime > timeoutMs) {
          console.warn('⚠️ Network stabilization timeout - proceeding anyway')
          resolve(false)
          return
        }

        setTimeout(checkStabilization, pollInterval)
      }

      checkStabilization()
    })
  }

  /**
   * Connect to peer by their alias - enhanced with legacy UUID compatibility
   * Supports both modern alias-based and legacy UUID-based peer connections
   */
  /**
   * Validate alias format - more permissive than peer ID validation
   */
  private validateAlias(alias: string): string {
    if (!alias || typeof alias !== 'string') {
      throw new Error('Invalid alias: must be a non-empty string')
    }

    const sanitizedAlias = alias.trim()
    if (sanitizedAlias.length === 0) {
      throw new Error('Invalid alias: cannot be empty')
    }

    // Alias validation: allow alphanumeric, spaces, hyphens, underscores, dots
    // More permissive than peer ID validation to support user-friendly aliases
    if (!/^[a-zA-Z0-9\s._-]+$/.test(sanitizedAlias)) {
      throw new Error('Invalid alias: contains unsafe characters. Only letters, numbers, spaces, dots, hyphens, and underscores are allowed.')
    }

    // Length validation
    if (sanitizedAlias.length > 50) {
      throw new Error('Invalid alias: too long (max 50 characters)')
    }

    return sanitizedAlias
  }

  /**
   * Connect directly to a peer using alias (bypasses strict peer ID validation)
   */
  private async connectToPeerByAlias(targetAlias: string): Promise<boolean> {
    if (!this.initialized || !this.peer || !this.localSelfta) {
      throw new Error('PeerConnectionManager not initialized')
    }

    // Use alias validation instead of peer ID validation
    const sanitizedAlias = this.validateAlias(targetAlias)

    if (sanitizedAlias === this.localSelfta.alias) {
      throw new Error('Cannot connect to self')
    }

    if (this.connections.has(sanitizedAlias)) {
      console.log(`Already connected to '${sanitizedAlias}'`)
      return true
    }

    try {
      console.log(`🔄 Connecting to '${sanitizedAlias}' via alias...`)

      // MOBILE FIX: Enhanced connection configuration for mobile-desktop reliability
      const isMobileUserAgent = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

      // MOBILE FIX: Validate connection readiness before attempting
      if (!this.validateConnectionReadiness(isMobileUserAgent)) {
        throw new Error('Network not ready for new connections')
      }

      // MOBILE FIX: Progressive metadata to prevent message size issues
      const baseMetadata = {
        sourceAlias: this.localSelfta.alias,
        connectionMethod: 'direct-alias',
        timestamp: Date.now(),
        isMobile: isMobileUserAgent
      }

      // MOBILE FIX: Additional metadata only for desktop connections or when explicitly needed
      const enhancedMetadata = isMobileUserAgent ? baseMetadata : {
        ...baseMetadata,
        clientVersion: '1.0.0',
        capabilities: ['messages', 'files', 'voice', 'video']
      }

      const conn = this.peer.connect(sanitizedAlias, {
        reliable: true,
        serialization: 'json',
        metadata: enhancedMetadata
      })

      return this.establishConnection(conn, sanitizedAlias)
    } catch (error) {
      console.error(`Failed to connect to alias '${sanitizedAlias}':`, error)
      throw error
    }
  }

  async connectByAlias(alias: string): Promise<boolean> {
    if (!this.initialized) {
      const msg = 'Network not initialized. Please initialize first.'
      console.log(`❌ ${msg}`)
      toastManager.error('Network not ready', TOAST_GROUPS.NETWORK)
      return false
    }

    if (!alias || alias.trim() === '') {
      const msg = 'Please enter a valid alias'
      console.log(`❌ ${msg}`)
      toastManager.error('Please enter a valid alias', TOAST_GROUPS.CONNECTION)
      return false
    }

    const cleanAlias = alias.trim()

    // STEP 1: Direct alias connection (primary method for alias-based peers)
    console.log(`🎯 Attempting direct alias connection to '${cleanAlias}'...`)
    toastManager.info(`Connecting to ${cleanAlias}...`, TOAST_GROUPS.CONNECTION, {
      icon: '🔄',
      duration: 3000,
      style: {
        background: '#dbeafe',
        color: '#1e40af'
      }
    })

    // Wait for network stabilization before attempting connection
    const networkStabilized = await this.waitForNetworkStabilization(8000)
    if (!networkStabilized) {
      console.warn(`⚠️ Network not yet stabilized, attempting connection anyway...`)
    }

    const directResult = await this.connectToPeerByAlias(cleanAlias)
    if (directResult) {
      console.log(`✅ Direct alias connection to '${cleanAlias}' successful!`)
      toastManager.success(`Connected to ${cleanAlias}`, TOAST_GROUPS.CONNECTION, {
        duration: 4000,
        icon: '✅'
      })
      return true
    }

    // STEP 2: Local registry lookup (for previously connected peers)
    console.log(`🔍 Direct connection failed, checking local registry...`)
    let peerId = this.aliasRegistry.getPeerId(cleanAlias)

    if (peerId && peerId !== cleanAlias) {
      console.log(`📋 Found '${cleanAlias}' in local registry: ${peerId.slice(0, 8)}...`)
      const registryResult = await this.connectToPeerWithRetry(peerId, cleanAlias)
      if (registryResult) {
        console.log(`✅ Connected to '${cleanAlias}' via local registry`)
        toastManager.success(`Connected to ${cleanAlias}`, TOAST_GROUPS.CONNECTION, {
          duration: 4000,
          icon: '✅'
        })
        return true
      }
    }

    // STEP 3: Network discovery (for legacy UUID-based peers and complex networks)
    if (this.connections.size > 0) {
      console.log(`🌐 Attempting network discovery for '${cleanAlias}'...`)
      toastManager.info(`Searching network for ${cleanAlias}...`, TOAST_GROUPS.CONNECTION, {
        icon: '🔍',
        duration: 4000,
        style: {
          background: '#fef3c7',
          color: '#92400e'
        }
      })

      peerId = await this.aliasRegistry.discoverAliasWithRetry(
        cleanAlias,
        this.connections,
        (attempt, maxAttempts) => {
          console.log(`🔍 Network search attempt ${attempt}/${maxAttempts} for '${cleanAlias}'...`)
        }
      )

      if (peerId && peerId !== cleanAlias) {
        console.log(`✅ Network discovery found '${cleanAlias}': ${peerId.slice(0, 8)}...`)
        // CRITICAL FIX: Use enhanced connection method with better compatibility
        const discoveryResult = await this.connectToPeerWithRetry(peerId, cleanAlias)
        if (discoveryResult) {
          console.log(`✅ Connected to '${cleanAlias}' via network discovery`)
          toastManager.success(`Connected to ${cleanAlias}`, TOAST_GROUPS.CONNECTION, {
            duration: 4000,
            icon: '✅'
          })
          return true
        }
      }
    }

    // All connection attempts failed - provide helpful message
    const isUsingAliasAsPeerId = this.localSelfta?.id === this.localSelfta?.alias
    const helpMessage = isUsingAliasAsPeerId
      ? `Unable to connect to '${cleanAlias}'. Make sure they are online and using the same network.`
      : `Unable to connect to '${cleanAlias}'. For best results, both users should set up aliases before connecting.`

    console.log(`❌ ${helpMessage}`)
    toastManager.error(`Could not connect to ${cleanAlias}`, TOAST_GROUPS.CONNECTION, {
      duration: 5000,
      icon: '❌'
    })
    return false
  }

  /**
   * Enhanced connection method with retry logic and legacy compatibility
   * Handles both alias-based and UUID-based peer connections
   */
  private async connectToPeerWithRetry(targetPeerId: string, alias?: string): Promise<boolean> {
    const maxRetries = 2
    const retryDelay = 2000 // 2 seconds between retries

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Connection attempt ${attempt}/${maxRetries} to '${alias || targetPeerId}'...`)

        // Minimal connection parameters to prevent message size issues
        const conn = this.peer!.connect(targetPeerId, {
          reliable: true,
          serialization: 'json',
          // Minimal metadata for compatibility
          metadata: {
            sourceAlias: this.localSelfta!.alias,
            targetAlias: alias || 'unknown',
            connectionMethod: this.isUsingAliasAsPeerId() ? 'direct-alias' : 'legacy-id',
            timestamp: Date.now(),
            attempt
          }
        })

        const success = await this.establishConnection(conn, targetPeerId)
        if (success) {
          // Register the successful alias-to-peerId mapping
          if (alias && alias !== targetPeerId) {
            this.aliasRegistry.registerPeerAlias(alias, targetPeerId)
          }
          return true
        }
      } catch (error) {
        console.warn(`⚠️ Connection attempt ${attempt} failed:`, error)
      }

      // Wait before retry (except on last attempt)
      if (attempt < maxRetries) {
        console.log(`⏳ Waiting ${retryDelay}ms before retry...`)
        await new Promise(resolve => setTimeout(resolve, retryDelay))
      }
    }

    console.error(`❌ All ${maxRetries} connection attempts failed for '${alias || targetPeerId}'`)
    return false
  }

  /**
   * Disconnect from a specific peer
   */
  disconnectFromPeer(peerId: string): boolean {
    const connectionData = this.connections.get(peerId)
    if (!connectionData) {
      return false
    }

    try {
      connectionData.connection.close()
      this.connections.delete(peerId)
      
      this.emit('disconnection', { 
        peerId, 
        reason: 'User initiated disconnection' 
      })
      
      console.log(`Disconnected from ${connectionData.info.alias || peerId}`)
      return true
    } catch (error) {
      console.error(`Error disconnecting from ${peerId}:`, error)
      return false
    }
  }

  /**
   * Disconnect from all peers
   */
  disconnectAll(): void {
    const peerIds = Array.from(this.connections.keys())
    peerIds.forEach(peerId => this.disconnectFromPeer(peerId))
    console.log(`Disconnected from all ${peerIds.length} peers`)
  }

  /**
   * Send data to a specific peer
   */
  sendToPeer(peerId: string, data: unknown): boolean {
    const connectionData = this.connections.get(peerId)
    if (!connectionData?.connection.open) {
      console.warn(`Cannot send data - no active connection to ${peerId}`)
      return false
    }

    try {
      connectionData.connection.send(data)
      return true
    } catch (error) {
      console.error(`Failed to send data to ${peerId}:`, error)
      return false
    }
  }

  /**
   * Broadcast data to all connected peers
   */
  broadcastToAll(data: unknown, excludePeerId?: string): number {
    let successCount = 0
    
    for (const [peerId, connectionData] of this.connections) {
      if (peerId !== excludePeerId && connectionData.connection.open) {
        if (this.sendToPeer(peerId, data)) {
          successCount++
        }
      }
    }
    
    return successCount
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): {
    isInitialized: boolean
    isNetworkStabilized: boolean
    localPeerId: string | null
    connectedPeers: string[]
    connectionCount: number
  } {
    return {
      isInitialized: this.initialized,
      isNetworkStabilized: this.isNetworkStabilized,
      localPeerId: this.localSelfta?.id ?? null,
      connectedPeers: Array.from(this.connections.keys()),
      connectionCount: this.connections.size
    }
  }

  /**
   * Check if network is ready for alias-based connections
   */
  isReadyForAliasConnections(): boolean {
    return this.initialized && this.isNetworkStabilized
  }

  /**
   * Check if the local peer is using alias as peer ID (direct alias connections enabled)
   */
  isUsingAliasAsPeerId(): boolean {
    return Boolean(
      this.localSelfta?.id === this.localSelfta?.alias &&
      this.localSelfta?.alias &&
      this.localSelfta.alias.trim().length > 0
    )
  }

  /**
   * Get connection method info for debugging and UI display
   */
  getConnectionMethodInfo(): {
    method: 'direct-alias' | 'legacy-id' | 'not-initialized'
    peerId: string | null
    alias: string | null
    canDirectConnect: boolean
  } {
    if (!this.initialized || !this.localSelfta) {
      return {
        method: 'not-initialized',
        peerId: null,
        alias: null,
        canDirectConnect: false
      }
    }

    const isUsingAlias = this.isUsingAliasAsPeerId()
    return {
      method: isUsingAlias ? 'direct-alias' : 'legacy-id',
      peerId: this.localSelfta.id,
      alias: this.localSelfta.alias,
      canDirectConnect: Boolean(isUsingAlias && this.isNetworkStabilized)
    }
  }

  /**
   * Get information about connected peers
   */
  getConnectedPeers(): PeerConnection[] {
    return Array.from(this.connections.values()).map(data => data.info)
  }

  /**
   * Handle incoming connection from another peer
   */
  private async handleIncomingConnection(conn: DataConnection): Promise<void> {
    console.log(`📞 Incoming connection from ${conn.peer}`)

    try {
      const success = await this.establishConnection(conn, conn.peer)
      if (!success) {
        console.warn(`Failed to establish connection with ${conn.peer}`)
        this.cleanupFailedConnection(conn, conn.peer)
      }
    } catch (error) {
      console.error(`Error handling incoming connection from ${conn.peer}:`, error)
      this.cleanupFailedConnection(conn, conn.peer)
    }
  }

  /**
   * Establish and configure a peer connection - enhanced for mobile compatibility
   */
  private async establishConnection(conn: DataConnection, peerId: string): Promise<boolean> {
    return new Promise((resolve) => {
      let isResolved = false

      // MOBILE FIX: Increased timeout for mobile networks with chunk-aware timing
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const mobileTimeout = isMobile
        ? this.connectionTimeout * 2.5 // 2.5x longer timeout for mobile to accommodate chunked handshakes
        : this.connectionTimeout

      const timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true
          console.warn(`⏰ Connection timeout to '${peerId}' after ${mobileTimeout}ms`)
          this.emit('connection-timeout', { peerId })
          resolve(false)
        }
      }, mobileTimeout)

      conn.on('open', () => {
        if (isResolved) return
        isResolved = true
        clearTimeout(timeout)

        // MOBILE FIX: Enhanced alias detection with better fallback logic
        const isAliasBasedConnection = this.isLikelyAliasBasedPeerId(peerId)
        let peerAlias = this.aliasRegistry.getAlias(peerId)

        // MOBILE FIX: Improved alias resolution with connection metadata support
        if (!peerAlias || peerAlias === 'unknown') {
          peerAlias = this.resolveInitialAlias(peerId, conn.metadata, isAliasBasedConnection)

          // MOBILE FIX: Immediately register resolved alias
          if (peerAlias && peerAlias !== 'unknown') {
            this.aliasRegistry.registerPeerAlias(peerAlias, peerId)
            console.log(`✅ Initial alias resolved and registered: '${peerAlias}' for ${peerId.slice(0, 8)}...`)
          }
        }

        const displayName = peerAlias

        // MOBILE FIX: Create connection with validation flag
        const peerConnection: PeerConnection = {
          id: peerId,
          peerId,
          alias: peerAlias,
          displayName,
          selfta: {
            id: peerId,
            alias: peerAlias,
            displayName,
            statusMessage: 'Connecting...',
            createdAt: new Date(),
            lastUpdated: new Date()
          },
          connection: conn,
          connectionState: 'connected',
          connectionQuality: 'good',
          isConnected: true,
          connectedAt: new Date(),
          lastSeen: new Date(),
          // MOBILE FIX: Add validation flags
          hasValidAlias: peerAlias !== 'unknown' && !peerAlias.startsWith('User_'),
          pendingHandshake: true
        }

        this.connections.set(peerId, { connection: conn, info: peerConnection })

        // MOBILE FIX: Register initial alias immediately (with additional safety check)
        if (peerAlias && peerAlias !== 'unknown' && !peerAlias.startsWith('User_')) {
          this.aliasRegistry.registerPeerAlias(peerAlias, peerId)
          console.log(`✅ Initial alias confirmed: '${peerAlias}' for ${peerId.slice(0, 8)}...`)
        }

        // Setup connection event handlers first
        this.setupConnectionEvents(conn, peerId)

        // MOBILE FIX: Send handshake immediately for faster alias exchange
        this.sendHandshake(conn)

        // MOBILE FIX: Emit initial connection event
        this.emit('connection', peerConnection)

        const connectionType = isAliasBasedConnection ? 'direct alias' : 'legacy'
        console.log(`✅ Connected to '${peerAlias}' via ${connectionType} (${peerId.slice(0, 8)}...) - ${peerAlias === 'unknown' ? 'awaiting alias' : 'alias confirmed'}`)

        // MOBILE FIX: Show appropriate status based on alias resolution
        if (peerAlias === 'unknown' || peerAlias.startsWith('User_')) {
          toastManager.info(`Connecting to peer...`, TOAST_GROUPS.CONNECTION, {
            duration: 1500,
            icon: '🔄'
          })
        } else {
          toastManager.success(`Connected to ${peerAlias}`, TOAST_GROUPS.CONNECTION, {
            duration: 3000,
            icon: '✅'
          })
        }

        resolve(true)
      })

      conn.on('error', (error) => {
        if (isResolved) return
        isResolved = true
        clearTimeout(timeout)
        console.error(`❌ Connection error with '${peerId}':`, error)

        // MOBILE FIX: Enhanced error handling with mobile-specific context
        const errorContext = {
          peerId,
          alias: this.aliasRegistry.getAlias(peerId) || 'unknown',
          error,
          timestamp: new Date(),
          isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
          errorType: this.categorizeConnectionError(error)
        }
        this.emit('error', errorContext)
        resolve(false)
      })
    })
  }

  /**
   * Setup event handlers for an established connection
   */
  private setupConnectionEvents(conn: DataConnection, peerId: string): void {
    conn.on('data', (data) => {
      this.handleIncomingData(data, peerId)
    })

    conn.on('close', () => {
      const connectionData = this.connections.get(peerId)
      console.log(`Connection closed with ${connectionData?.info.alias || peerId}`)
      
      this.connections.delete(peerId)
      this.emit('disconnection', { 
        peerId, 
        reason: 'Connection closed by peer' 
      })
      
      // Attempt reconnection if not user-initiated
      this.attemptReconnection(peerId)
    })

    conn.on('error', (error) => {
      console.error(`Connection error with ${peerId}:`, error)
      this.emit('error', { peerId, error })
    })
  }

  /**
   * Clean up failed connections
   */
  private cleanupFailedConnection(conn: DataConnection, peerId: string): void {
    try {
      // Remove from connections if exists
      this.connections.delete(peerId)

      // Close the connection if it's still open
      if (conn && conn.open) {
        conn.close()
      }

      // Emit disconnection event
      this.emit('disconnection', {
        peerId,
        reason: 'Connection failed'
      })

      console.log(`🧹 Cleaned up failed connection with ${peerId}`)
    } catch (error) {
      console.error(`Error cleaning up failed connection with ${peerId}:`, error)
    }
  }

  /**
   * Handle incoming data from peers
   */
  private handleIncomingData(data: unknown, fromPeerId: string): void {
    try {
      const typedData = data as { type?: string; [key: string]: unknown }

      // Get the connection to delegate to services
      const connectionData = this.connections.get(fromPeerId)

      // First, try to handle through AliasRegistryService
      if (connectionData && this.aliasRegistry.handleP2PData(data, connectionData.connection)) {
        console.log(`✅ Data handled by AliasRegistryService: ${typedData.type}`)
        return
      }

      // Second, try to handle through MessageService
      // CRITICAL FIX: Added null check to prevent calling handleP2PData on null/undefined messageService
      if (this.messageService && this.messageService.handleP2PData(data, fromPeerId)) {
        console.log(`✅ Data handled by MessageService: ${typedData.type}`)
        return
      }
      
      // Third, try to handle through MediaService
      if (this.mediaService.handleP2PData(data, fromPeerId)) {
        console.log(`✅ Data handled by MediaService: ${typedData.type}`)
        return
      }
      
      // Fourth, try to handle through FileTransferService
      if (this.fileTransferService.handleP2PData(data, fromPeerId, connectionData?.connection)) {
        console.log(`✅ Data handled by FileTransferService: ${typedData.type}`)
        return
      }
      
      // Handle PeerConnectionManager-specific data types
      switch (typedData.type) {
        case 'handshake':
        case 'philosophical-handshake': // Support original demo format
          this.handleHandshake(typedData, fromPeerId)
          break
        
        case 'handshake-reply':
        case 'philosophical-handshake-reply': // Support original demo format
          this.handleHandshakeReply(typedData, fromPeerId)
          break
        
        case 'alias-update':
          this.handleAliasUpdate(typedData, fromPeerId)
          break

        case 'chunked-handshake-start':
          this.handleChunkedHandshakeStart(typedData, fromPeerId)
          break

        case 'chunked-handshake-data':
          this.handleChunkedHandshakeData(typedData, fromPeerId)
          break

        case 'request-minimal-handshake':
          this.handleMinimalHandshakeRequest(typedData, fromPeerId)
          break

        default:
          // Forward to application layer
          this.emit('data', { peerId: fromPeerId, data })
      }
    } catch (error) {
      console.error(`Error handling data from ${fromPeerId}:`, error)
    }
  }

  /**
   * Send handshake to newly connected peer - with binary channel support for large payloads
   */
  private sendHandshake(conn: DataConnection): void {
    if (!this.localSelfta) return

    // Detect mobile user agent for enhanced mobile-desktop compatibility
    const isMobileUserAgent = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    // MOBILE FIX: Optimize avatar data for transmission to prevent "Message too big" errors
    const optimizedSelfta = this.optimizeSelftaForTransmission(this.localSelfta)

    // MOBILE FIX: Ultra-lightweight handshake for mobile compatibility
    const handshakeData = {
      type: 'philosophical-handshake', // Use original demo format for compatibility
      from: this.localSelfta.id,
      alias: this.localSelfta.alias,
      selfta: optimizedSelfta,
      timestamp: Date.now(),
      // MOBILE FIX: Minimal metadata only
      connectionMethod: this.localSelfta.id === this.localSelfta.alias ? 'direct-alias' : 'legacy-id',
      // Remove feature list and other non-essential data for mobile
      isMobile: isMobileUserAgent
    }

    // CRITICAL FIX: Check against PeerJS JSON channel limit (64KB)
    const handshakeSize = JSON.stringify(handshakeData).length
    const PEERJS_JSON_LIMIT = 60000 // 60KB to be safe, actual limit is ~64KB

    if (handshakeSize > PEERJS_JSON_LIMIT) {
      console.warn(`⚠️ Handshake size (${handshakeSize} bytes) exceeds PeerJS JSON limit (${PEERJS_JSON_LIMIT}), using chunked transmission`)
      this.sendLargeHandshakeViaChunks(conn, handshakeData)
      return
    }

    const isAliasConnection = this.localSelfta.id === this.localSelfta.alias
    const deviceType = isMobileUserAgent ? 'mobile' : 'desktop'
    console.log(`🤝 Sending ${deviceType} handshake to ${conn.peer} (${isAliasConnection ? 'alias-based' : 'legacy'}) with alias: ${this.localSelfta.alias} (${handshakeSize} bytes)`)

    try {
      conn.send(handshakeData)
    } catch (error) {
      console.error(`❌ Failed to send handshake to ${conn.peer}:`, error)
      // Try binary channel as fallback
      console.log(`🔄 Attempting binary channel fallback for ${conn.peer}`)
      this.sendLargeHandshakeViaChunks(conn, handshakeData)
    }
  }

  /**
   * Handle handshake from peer - enhanced for mobile compatibility and alias timing
   */
  private handleHandshake(data: { [key: string]: unknown }, fromPeerId: string): void {
    const connectionData = this.connections.get(fromPeerId)
    if (!connectionData) {
      console.warn(`⚠️ Received handshake from unknown peer: ${fromPeerId}`)
      return
    }

    let peerAlias = 'unknown'
    let peerSelfta: SelfTA | null = null
    let connectionMethod = 'unknown'
    let isMinimalHandshake = false
    let isEmergencyHandshake = false

    // MOBILE FIX: Handle different handshake formats including minimal/emergency
    if (data.emergency) {
      isEmergencyHandshake = true
      peerAlias = (data.alias as string) || `Emergency_${fromPeerId.slice(-8)}`
      console.log(`🚨 Emergency handshake from ${fromPeerId} with alias: ${peerAlias}`)

      // MOBILE FIX: Immediately register emergency alias to prevent "Unknown" display
      if (peerAlias && peerAlias !== 'unknown') {
        this.aliasRegistry.registerPeerAlias(peerAlias, fromPeerId)
      }
    } else if (data.minimal) {
      isMinimalHandshake = true
      console.log(`📱 Minimal handshake from ${fromPeerId}`)
    }

    // Handle both new format (selfta object) and original demo format (alias field)
    if (data.selfta && typeof data.selfta === 'object') {
      peerSelfta = data.selfta as SelfTA
      peerAlias = peerSelfta.alias || peerAlias
    } else if (data.alias && typeof data.alias === 'string') {
      peerAlias = data.alias
      // Create SelfTA from alias - enhanced for mobile compatibility
      peerSelfta = this.createSelftaFromAlias(fromPeerId, peerAlias, isMinimalHandshake || isEmergencyHandshake)
    } else {
      // LEGACY COMPATIBILITY: Handle peers that don't send alias information
      console.log(`⚠️ Legacy handshake from ${fromPeerId} - no alias provided, creating fallback`)

      // MOBILE FIX: Better alias extraction logic
      peerAlias = this.extractAliasFromPeerId(fromPeerId)
      peerSelfta = this.createSelftaFromAlias(fromPeerId, peerAlias, true)
      connectionMethod = 'legacy-no-alias'
    }

    // Extract connection method from handshake
    if (data.connectionMethod && typeof data.connectionMethod === 'string') {
      connectionMethod = data.connectionMethod
    }

    if (peerSelfta && peerAlias !== 'unknown') {
      // MOBILE FIX: Immediate alias registration to prevent "Unknown User" display
      this.aliasRegistry.registerPeerAlias(peerAlias, fromPeerId)
      console.log(`✅ Alias registered immediately: '${peerAlias}' -> ${fromPeerId.slice(0, 8)}...`)

      // MOBILE FIX: Update connection info immediately for UI consistency
      this.updateConnectionInfoImmediately(connectionData, peerSelfta, peerAlias)

      // Detect if this is an alias-based connection
      const isAliasBasedConnection = fromPeerId === peerAlias || connectionMethod === 'direct-alias'
      const connectionTypeLabel = isAliasBasedConnection ? 'alias-based' : 'legacy'
      const handshakeType = isEmergencyHandshake ? 'emergency' : isMinimalHandshake ? 'minimal' : 'full'

      console.log(`🤝 ${handshakeType.charAt(0).toUpperCase() + handshakeType.slice(1)} handshake received from ${peerAlias} (${fromPeerId.slice(0, 8)}...) - ${connectionTypeLabel} connection`)

      // Send handshake reply if this was the initial handshake
      if (data.type === 'philosophical-handshake' || data.type === 'handshake') {
        // MOBILE FIX: Immediate handshake reply for faster alias resolution
        this.sendHandshakeReply(connectionData.connection, fromPeerId)
      }

      // MOBILE FIX: Emit connection update immediately after alias registration
      this.emit('connection', connectionData.info)

      // MOBILE FIX: Additional UI update with slight delay to ensure consistency
      setTimeout(() => {
        this.emit('connection', connectionData.info)
      }, 50) // Reduced delay for better responsiveness
    } else {
      console.error(`❌ Failed to process handshake from ${fromPeerId} - invalid alias or SelfTA data`)
    }
  }

  /**
   * Send handshake reply to peer
   */
  private sendHandshakeReply(conn: DataConnection, toPeerId: string): void {
    if (!this.localSelfta) return

    const replyData = {
      type: 'philosophical-handshake-reply',
      from: this.localSelfta.id,
      alias: this.localSelfta.alias,
      selfta: this.localSelfta,
      message: 'Handshake acknowledged - connection established',
      timestamp: Date.now()
    }

    console.log(`🤝 Sending handshake reply to ${toPeerId.slice(0, 8)}... with alias: ${this.localSelfta.alias}`)
    conn.send(replyData)
  }

  /**
   * Handle handshake reply from peer
   */
  private handleHandshakeReply(data: { [key: string]: unknown }, fromPeerId: string): void {
    // Handle reply the same way as initial handshake for alias registration
    this.handleHandshake(data, fromPeerId)
    console.log(`✅ Handshake reply processed from ${fromPeerId.slice(0, 8)}...`)
  }

  /**
   * Handle alias update from peer
   */
  private handleAliasUpdate(data: { [key: string]: unknown }, fromPeerId: string): void {
    if (data.alias && typeof data.alias === 'string') {
      this.aliasRegistry.updatePeerAlias(data.alias, fromPeerId)
      
      const connectionData = this.connections.get(fromPeerId)
      if (connectionData) {
        connectionData.info.alias = data.alias
        connectionData.info.selfta.alias = data.alias
      }
      
      console.log(`🏷️ Alias updated for ${fromPeerId}: ${data.alias}`)
    }
  }

  /**
   * Attempt to reconnect to signaling server
   */
  private attemptReconnectToSignaling(): void {
    if (!this.peer || this.peer.destroyed) {
      console.warn('Cannot reconnect: peer instance is null or destroyed')
      return
    }

    setTimeout(() => {
      try {
        if (this.peer && !this.peer.destroyed) {
          this.peer.reconnect()
          console.log('Attempting to reconnect to signaling server...')
        } else {
          console.warn('Skipping reconnect: peer was destroyed during timeout')
        }
      } catch (error) {
        console.error('Failed to reconnect to signaling server:', error)
      }
    }, 3000)
  }

  /**
   * Attempt to reconnect to a disconnected peer with exponential backoff and jitter
   */
  private attemptReconnection(peerId: string): void {
    const attempts = this.reconnectAttempts.get(peerId) || 0

    if (attempts >= this.maxReconnectAttempts) {
      console.log(`Max reconnection attempts reached for ${peerId}`)
      this.reconnectAttempts.delete(peerId)
      this.emit('reconnection-failed', { peerId, attempts })
      return
    }

    // Exponential backoff with jitter: base delay * (2 ^ attempts) + random jitter
    const baseDelay = 2000 // Start with 2 seconds
    const exponentialDelay = baseDelay * Math.pow(2, attempts)
    const jitter = Math.random() * 1000 // Add up to 1 second of random jitter
    const delay = Math.min(exponentialDelay + jitter, 30000) // Cap at 30 seconds

    console.log(`Scheduling reconnection to ${peerId} in ${Math.round(delay)}ms (attempt ${attempts + 1}/${this.maxReconnectAttempts})`)

    setTimeout(async () => {
      try {
        console.log(`🔄 Attempting reconnection to ${peerId} (attempt ${attempts + 1}/${this.maxReconnectAttempts})`)
        this.reconnectAttempts.set(peerId, attempts + 1)

        const success = await this.connectToPeer(peerId)
        if (success) {
          this.reconnectAttempts.delete(peerId)
          console.log(`✅ Successfully reconnected to ${peerId}`)
          this.emit('reconnection-success', { peerId, attempts: attempts + 1 })
        } else {
          // Schedule next attempt
          this.attemptReconnection(peerId)
        }
      } catch (error) {
        console.error(`❌ Reconnection attempt to ${peerId} failed:`, error)
        // Schedule next attempt
        this.attemptReconnection(peerId)
      }
    }, delay)
  }

  /**
   * Event system implementation
   */
  on<T = unknown>(event: string, handler: EventHandler<T>): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, [])
    }
    this.eventHandlers.get(event)!.push(handler as EventHandler)
  }

  off<T = unknown>(event: string, handler: EventHandler<T>): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      const index = handlers.indexOf(handler as EventHandler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  private emit<T = unknown>(event: string, data: T): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      const eventData: P2PEvent & { data: T } = {
        type: event as P2PEvent['type'],
        peerId: '',
        data,
        timestamp: new Date()
      }
      
      handlers.forEach(handler => {
        try {
          handler(eventData)
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error)
        }
      })
    }
  }

  /**
   * Get MessageService instance for sending messages
   */
  getMessageService(): MessageService {
    return this.messageService
  }

  /**
   * Get MediaService instance for media communication
   */
  getMediaService(): MediaService {
    return this.mediaService
  }

  /**
   * Get FileTransferService instance for file sharing
   */
  getFileTransferService(): FileTransferService {
    return this.fileTransferService
  }

  /**
   * Update MessageService instance (called by store)
   * CRITICAL FIX: Ensures same MessageService instance is used for both sending and receiving
   */
  updateMessageService(messageService: MessageService): void {
    this.messageService = messageService
  }

  /**
   * Get local peer ID
   */
  getPeerId(): string | null {
    return this.peer?.id || null
  }

  /**
   * Get local alias
   */
  getAlias(): string | null {
    return this.localSelfta?.alias || null
  }

  /**
   * MOBILE FIX: Optimize SelfTA data for transmission to prevent size issues
   */
  private optimizeSelftaForTransmission(selfta: SelfTA, forceMinimal: boolean = false) {
    const optimized = {
      id: selfta.id,
      alias: selfta.alias,
      displayName: selfta.displayName || selfta.alias,
      statusMessage: selfta.statusMessage || 'Connected',
      profileImage: this.getOptimizedProfileImage(selfta.profileImage || undefined, forceMinimal), // Smart avatar compression
      createdAt: selfta.createdAt,
      lastUpdated: selfta.lastUpdated
    }

    // MOBILE FIX: Aggressive truncation for mobile compatibility
    if (optimized.displayName && optimized.displayName.length > 30) {
      optimized.displayName = optimized.displayName.substring(0, 27) + '...'
    }

    if (optimized.statusMessage && optimized.statusMessage.length > 50) {
      optimized.statusMessage = optimized.statusMessage.substring(0, 47) + '...'
    }

    return optimized
  }

  /**
   * MOBILE FIX: Smart profile image optimization - remove for minimal handshakes to prevent corruption
   */
  private getOptimizedProfileImage(profileImage: string | undefined, forceMinimal: boolean): string | undefined {
    if (!profileImage) return undefined

    // For normal handshakes, return as-is
    if (!forceMinimal) return profileImage

    // CRITICAL FIX: For minimal handshakes, completely remove avatar to prevent base64 corruption
    // The previous truncation approach was corrupting base64 data causing handshake failures
    console.log(`📦 Avatar removed for minimal handshake to prevent corruption (was ${profileImage.length} bytes)`)
    return undefined
  }

  /**
   * MOBILE FIX: Send minimal handshake when full handshake is too large
   */
  private sendMinimalHandshake(conn: DataConnection, optimizedSelfta: any): void {
    const minimalHandshake = {
      type: 'philosophical-handshake',
      from: this.localSelfta!.id,
      alias: this.localSelfta!.alias,
      selfta: {
        id: optimizedSelfta.id,
        alias: optimizedSelfta.alias,
        displayName: optimizedSelfta.displayName || optimizedSelfta.alias,
        statusMessage: optimizedSelfta.statusMessage || '',
        profileImage: optimizedSelfta.profileImage // Include optimized avatar if available
      },
      timestamp: Date.now(),
      connectionMethod: this.localSelfta!.id === this.localSelfta!.alias ? 'direct-alias' : 'legacy-id',
      minimal: true
    }

    console.log(`📱 Sending minimal handshake to ${conn.peer} (${JSON.stringify(minimalHandshake).length} bytes)`)

    try {
      conn.send(minimalHandshake)
    } catch (error) {
      console.error(`❌ Failed to send minimal handshake to ${conn.peer}:`, error)
      // Emergency: send only essential alias data
      try {
        conn.send({
          type: 'philosophical-handshake',
          from: this.localSelfta!.id,
          alias: this.localSelfta!.alias,
          timestamp: Date.now(),
          emergency: true
        })
        console.log(`🚨 Emergency handshake sent to ${conn.peer}`)
      } catch (emergencyError) {
        console.error(`❌ Emergency handshake also failed to ${conn.peer}:`, emergencyError)
      }
    }
  }

  // Map to store incoming chunked handshakes
  private chunkBuffers = new Map<string, {
    chunks: Map<number, string>,
    totalChunks: number,
    receivedChunks: number,
    peerId: string,
    timeoutId?: NodeJS.Timeout,
    startTime: number
  }>()

  /**
   * Handle start of chunked handshake
   */
  private handleChunkedHandshakeStart(data: any, fromPeerId: string): void {
    console.log(`📨 Starting chunked handshake from ${fromPeerId}, ${data.totalChunks} chunks, ${data.totalSize} bytes`)

    // Clear any existing timeout for this chunk ID
    const existingBuffer = this.chunkBuffers.get(data.chunkId)
    if (existingBuffer?.timeoutId) {
      clearTimeout(existingBuffer.timeoutId)
    }

    // MOBILE FIX: Extended timeout for chunked handshakes (30 seconds for mobile)
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    const chunkTimeout = isMobile ? 30000 : 15000 // 30s for mobile, 15s for desktop

    const timeoutId = setTimeout(() => {
      console.error(`⏰ Chunked handshake timeout from ${fromPeerId} for chunk ${data.chunkId}`)
      this.chunkBuffers.delete(data.chunkId)

      // Request minimal handshake as fallback
      this.requestMinimalHandshakeFallback(fromPeerId)
    }, chunkTimeout)

    // Initialize chunk buffer
    this.chunkBuffers.set(data.chunkId, {
      chunks: new Map(),
      totalChunks: data.totalChunks,
      receivedChunks: 0,
      peerId: fromPeerId,
      timeoutId,
      startTime: Date.now()
    })
  }

  /**
   * Handle chunked handshake data
   */
  private handleChunkedHandshakeData(data: any, fromPeerId: string): void {
    const buffer = this.chunkBuffers.get(data.chunkId)
    if (!buffer) {
      console.error(`❌ Received chunk ${data.chunkIndex} but no buffer found for ${data.chunkId} from ${fromPeerId}`)
      return
    }

    // Store the chunk
    buffer.chunks.set(data.chunkIndex, data.data)
    buffer.receivedChunks++

    console.log(`📦 Received chunk ${data.chunkIndex + 1}/${data.totalChunks} from ${fromPeerId}`)

    // Check if all chunks received
    if (buffer.receivedChunks === buffer.totalChunks) {
      console.log(`✅ All chunks received from ${fromPeerId}, reassembling handshake`)

      // Clear timeout since we got all chunks
      if (buffer.timeoutId) {
        clearTimeout(buffer.timeoutId)
      }

      this.reassembleChunkedHandshake(data.chunkId, fromPeerId)
    }
  }

  /**
   * Reassemble chunked handshake and process
   */
  private reassembleChunkedHandshake(chunkId: string, fromPeerId: string): void {
    try {
      const buffer = this.chunkBuffers.get(chunkId)
      if (!buffer) {
        throw new Error(`Chunk buffer not found for ${chunkId}`)
      }

      // Reassemble chunks in order
      let reassembledJson = ''
      for (let i = 0; i < buffer.totalChunks; i++) {
        const chunk = buffer.chunks.get(i)
        if (!chunk) {
          throw new Error(`Missing chunk ${i} for ${chunkId}`)
        }
        reassembledJson += chunk
      }

      console.log(`🔧 Reassembled handshake from ${fromPeerId} (${reassembledJson.length} bytes)`)

      // Parse and process the handshake
      const handshakeData = JSON.parse(reassembledJson)
      this.handleHandshake(handshakeData, fromPeerId)

      // Clean up buffer
      this.chunkBuffers.delete(chunkId)

    } catch (error) {
      console.error(`❌ Failed to reassemble chunked handshake from ${fromPeerId}:`, error)

      // Clean up failed buffer
      this.chunkBuffers.delete(chunkId)

      // Request minimal handshake as fallback
      const connectionData = this.connections.get(fromPeerId)
      if (connectionData?.connection) {
        try {
          connectionData.connection.send({
            type: 'request-minimal-handshake',
            from: this.localSelfta!.id,
            timestamp: Date.now()
          })
          console.log(`🔄 Requested minimal handshake fallback from ${fromPeerId}`)
        } catch (fallbackError) {
          console.error(`❌ Failed to request minimal handshake from ${fromPeerId}:`, fallbackError)
        }
      }
    }
  }

  /**
   * Handle request for minimal handshake (fallback when binary fails)
   */
  private handleMinimalHandshakeRequest(data: any, fromPeerId: string): void {
    console.log(`🔄 Received minimal handshake request from ${fromPeerId}`)

    const connectionData = this.connections.get(fromPeerId)
    if (connectionData?.connection && this.localSelfta) {
      const optimizedSelfta = this.optimizeSelftaForTransmission(this.localSelfta, true) // Force minimal
      this.sendMinimalHandshake(connectionData.connection, optimizedSelfta)
    }
  }

  /**
   * MOBILE FIX: Request minimal handshake as fallback when chunked transmission fails
   */
  private requestMinimalHandshakeFallback(fromPeerId: string): void {
    console.log(`🚨 Requesting minimal handshake fallback from ${fromPeerId}`)

    const connectionData = this.connections.get(fromPeerId)
    if (connectionData?.connection && connectionData.connection.open) {
      try {
        connectionData.connection.send({
          type: 'request-minimal-handshake',
          from: this.localSelfta!.id,
          timestamp: Date.now(),
          reason: 'chunked-handshake-timeout'
        })
      } catch (error) {
        console.error(`❌ Failed to request minimal handshake fallback from ${fromPeerId}:`, error)
      }
    }
  }

  /**
   * Send large handshake via chunked messages to bypass PeerJS JSON size limits
   */
  private sendLargeHandshakeViaChunks(conn: DataConnection, handshakeData: any): void {
    try {
      const handshakeJson = JSON.stringify(handshakeData)
      const CHUNK_SIZE = 10000 // 10KB data + wrapper overhead = safe for PeerJS

      // Detect mobile device for optimized transmission timing
      const isMobileUserAgent = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

      // Calculate number of chunks needed
      const totalChunks = Math.ceil(handshakeJson.length / CHUNK_SIZE)
      const chunkId = Date.now().toString()

      console.log(`📨 Sending chunked handshake to ${conn.peer} in ${totalChunks} chunks (${handshakeJson.length} bytes total) - ${isMobileUserAgent ? 'MOBILE' : 'DESKTOP'} mode`)

      // Send chunk header first
      const chunkHeader = {
        type: 'chunked-handshake-start',
        from: this.localSelfta!.id,
        alias: this.localSelfta!.alias,
        chunkId,
        totalChunks,
        totalSize: handshakeJson.length,
        timestamp: Date.now()
      }

      conn.send(chunkHeader)
      console.log(`📦 Sent chunk header to ${conn.peer}`)

      // Send chunks with delay to prevent overwhelming the connection
      for (let i = 0; i < totalChunks; i++) {
        setTimeout(() => {
          const start = i * CHUNK_SIZE
          const end = Math.min(start + CHUNK_SIZE, handshakeJson.length)
          const chunk = handshakeJson.substring(start, end)

          const chunkMessage = {
            type: 'chunked-handshake-data',
            chunkId,
            chunkIndex: i,
            totalChunks,
            data: chunk,
            isLast: i === totalChunks - 1
          }

          try {
            // Validate chunk size before sending
            const chunkSize = JSON.stringify(chunkMessage).length
            if (chunkSize > 15000) { // 15KB safety limit
              console.error(`❌ Chunk ${i + 1} too large (${chunkSize} bytes), using minimal handshake`)
              const optimizedSelfta = this.optimizeSelftaForTransmission(this.localSelfta!, true)
              this.sendMinimalHandshake(conn, optimizedSelfta)
              return
            }

            conn.send(chunkMessage)
            console.log(`📦 Sent chunk ${i + 1}/${totalChunks} to ${conn.peer} (${chunkSize} bytes)`)
          } catch (chunkError) {
            console.error(`❌ Failed to send chunk ${i + 1} to ${conn.peer}:`, chunkError)
            // On chunk failure, fall back to minimal handshake
            const optimizedSelfta = this.optimizeSelftaForTransmission(this.localSelfta!, true)
            this.sendMinimalHandshake(conn, optimizedSelfta)
            return
          }
        }, i * (isMobileUserAgent ? 300 : 100)) // 300ms delay for mobile, 100ms for desktop
      }

    } catch (error) {
      console.error(`❌ Failed to send chunked handshake to ${conn.peer}:`, error)

      // Ultimate fallback - send minimal handshake without images
      console.log(`🚨 Using minimal handshake as ultimate fallback for ${conn.peer}`)
      const optimizedSelfta = this.optimizeSelftaForTransmission(this.localSelfta!, true) // Force minimal
      this.sendMinimalHandshake(conn, optimizedSelfta)
    }
  }

  /**
   * Send message to a specific peer
   */
  async sendMessage(message: string, targetPeerId: string): Promise<boolean> {
    if (!this.localSelfta) {
      throw new Error('Local SelfTA not initialized')
    }

    const connectionData = this.connections.get(targetPeerId)
    if (!connectionData?.connection.open) {
      throw new Error(`No active connection to ${targetPeerId}`)
    }

    return this.messageService.sendMessage(
      message,
      targetPeerId,
      this.peer?.id || '',
      this.localSelfta.alias,
      connectionData.connection
    )
  }

  /**
   * Initiate voice call to a specific peer
   */
  async initiateVoiceCall(targetPeerId: string): Promise<boolean> {
    if (!this.localSelfta) {
      throw new Error('Local SelfTA not initialized')
    }

    const connectionData = this.connections.get(targetPeerId)
    if (!connectionData?.connection.open) {
      throw new Error(`No active connection to ${targetPeerId}`)
    }

    return this.mediaService.initiateVoiceCall(
      targetPeerId,
      connectionData.info.alias || 'Unknown',
      this.peer?.id || '',
      this.localSelfta.alias,
      connectionData.connection,
      // CRITICAL FIX: Clean string "null" to actual null
      (connectionData.info.selfta?.profileImage === "null" ||
       connectionData.info.selfta?.profileImage === null ||
       connectionData.info.selfta?.profileImage === undefined ||
       connectionData.info.selfta?.profileImage === "") ? null : connectionData.info.selfta?.profileImage
    )
  }

  /**
   * Initiate video call to a specific peer
   */
  async initiateVideoCall(targetPeerId: string, localVideoElement?: HTMLVideoElement): Promise<boolean> {
    if (!this.localSelfta) {
      throw new Error('Local SelfTA not initialized')
    }

    const connectionData = this.connections.get(targetPeerId)
    if (!connectionData?.connection.open) {
      throw new Error(`No active connection to ${targetPeerId}`)
    }

    return this.mediaService.initiateVideoCall(
      targetPeerId,
      connectionData.info.alias || 'Unknown',
      this.peer?.id || '',
      this.localSelfta.alias,
      connectionData.connection,
      localVideoElement,
      // CRITICAL FIX: Clean string "null" to actual null
      (connectionData.info.selfta?.profileImage === "null" ||
       connectionData.info.selfta?.profileImage === null ||
       connectionData.info.selfta?.profileImage === undefined ||
       connectionData.info.selfta?.profileImage === "") ? null : connectionData.info.selfta?.profileImage
    )
  }

  /**
   * Start screen sharing with a specific peer
   */
  async startScreenShare(targetPeerId: string): Promise<boolean> {
    if (!this.localSelfta) {
      throw new Error('Local SelfTA not initialized')
    }

    const connectionData = this.connections.get(targetPeerId)
    if (!connectionData?.connection.open) {
      throw new Error(`No active connection to ${targetPeerId}`)
    }

    return this.mediaService.startScreenShare(
      targetPeerId,
      connectionData.info.alias || 'Unknown',
      this.peer?.id || '',
      this.localSelfta.alias,
      connectionData.connection
    )
  }

  /**
   * Send file to a specific peer
   */
  async sendFile(file: File, targetPeerId: string): Promise<string> {
    if (!this.localSelfta) {
      throw new Error('Local SelfTA not initialized')
    }

    const connectionData = this.connections.get(targetPeerId)
    if (!connectionData?.connection.open) {
      throw new Error(`No active connection to ${targetPeerId}`)
    }

    return this.fileTransferService.initiateFileTransfer(
      file,
      targetPeerId,
      connectionData.info.alias || 'Unknown',
      this.peer?.id || '',
      this.localSelfta.alias,
      connectionData.connection
    )
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.disconnectAll()
    
    if (this.peer) {
      this.peer.destroy()
      this.peer = null
    }
    
    this.connections.clear()
    this.eventHandlers.clear()
    this.reconnectAttempts.clear()
    this.initialized = false
    
    // Cleanup services
    this.aliasRegistry.cleanup()
    this.messageService.cleanup()
    this.mediaService.cleanup()
    this.fileTransferService.cleanup()
    
    console.log('PeerConnectionManager destroyed')
  }

  /**
   * MOBILE FIX: Create SelfTA from alias with mobile-optimized defaults
   */
  private createSelftaFromAlias(peerId: string, alias: string, isMinimal: boolean): SelfTA {
    return {
      id: peerId,
      alias,
      displayName: alias,
      statusMessage: isMinimal ? 'Connected' : 'Connected via P2P',
      createdAt: new Date(),
      lastUpdated: new Date()
    }
  }

  /**
   * MOBILE FIX: Extract meaningful alias from peer ID
   */
  private extractAliasFromPeerId(peerId: string): string {
    // If peer ID looks like an alias (short, no dashes, no UUIDs)
    if (peerId.length <= 20 && !peerId.includes('-') && !peerId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)) {
      return peerId
    }

    // For UUID-style peer IDs, create a meaningful short alias
    return `User_${peerId.slice(-8)}`
  }

  /**
   * MOBILE FIX: Update connection info immediately to prevent UI flickering
   */
  private updateConnectionInfoImmediately(connectionData: any, peerSelfta: SelfTA, peerAlias: string): void {
    // Update all relevant fields immediately
    connectionData.info.selfta = peerSelfta
    connectionData.info.alias = peerAlias
    connectionData.info.displayName = peerSelfta.displayName || peerAlias
    connectionData.info.lastSeen = new Date()

    // Mark as having valid alias data
    connectionData.info.hasValidAlias = true
    // Clear pending handshake flag
    connectionData.info.pendingHandshake = false

    // MOBILE FIX: Force update connection state to show as properly connected
    connectionData.info.connectionState = 'connected'

    // MOBILE FIX: Ensure avatar image is properly logged for debugging
    if (peerSelfta.profileImage) {
      const imageSize = peerSelfta.profileImage.length
      console.log(`🖼️ Avatar received for '${peerAlias}' (${imageSize} bytes)`)
    } else {
      console.log(`📷 No avatar for '${peerAlias}'`)
    }

    console.log(`🔄 Connection info updated immediately for '${peerAlias}' (${connectionData.info.peerId.slice(0, 8)}...)`)
  }

  /**
   * MOBILE FIX: Check if peer ID is likely alias-based
   */
  private isLikelyAliasBasedPeerId(peerId: string): boolean {
    return peerId.length <= 20 &&
           !peerId.includes('-') &&
           !peerId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) &&
           peerId.length > 3
  }

  /**
   * MOBILE FIX: Resolve initial alias from peer ID and metadata
   */
  private resolveInitialAlias(peerId: string, metadata: any, isAliasBasedConnection: boolean): string {
    // Check if peer ID looks like an alias
    if (isAliasBasedConnection) {
      console.log(`🎣 Alias-based connection detected: ${peerId}`)
      return peerId
    }

    // Try to get alias from connection metadata
    if (metadata && metadata.sourceAlias && typeof metadata.sourceAlias === 'string' && metadata.sourceAlias.trim()) {
      console.log(`🏷️ Source alias from metadata: ${metadata.sourceAlias}`)
      return metadata.sourceAlias.trim()
    }

    // Check target alias from metadata (for incoming connections)
    if (metadata && metadata.targetAlias && typeof metadata.targetAlias === 'string' && metadata.targetAlias.trim()) {
      console.log(`🏷️ Target alias from metadata: ${metadata.targetAlias}`)
      return metadata.targetAlias.trim()
    }

    // Enhanced fallback with better detection
    const fallback = this.extractAliasFromPeerId(peerId)
    console.log(`🔄 Fallback alias generated: ${fallback} for ${peerId.slice(0, 8)}...`)
    return fallback
  }

  /**
   * MOBILE FIX: Categorize connection errors for better handling
   */
  private categorizeConnectionError(error: any): string {
    const errorMessage = error?.message || error?.toString() || 'unknown'

    if (errorMessage.includes('Message too big')) {
      return 'message_size_exceeded'
    }
    if (errorMessage.includes('Connection failed') || errorMessage.includes('network')) {
      return 'network_error'
    }
    if (errorMessage.includes('timeout')) {
      return 'timeout_error'
    }
    if (errorMessage.includes('peer') && errorMessage.includes('not found')) {
      return 'peer_not_found'
    }

    return 'unknown_error'
  }

  /**
   * MOBILE FIX: Validate network readiness for new connections
   */
  private validateConnectionReadiness(isMobile: boolean): boolean {
    // Check if network is initialized and stabilized
    if (!this.initialized || !this.peer || this.peer.destroyed) {
      console.warn('⚠️ Network not initialized for new connections')
      return false
    }

    // MOBILE FIX: Additional checks for mobile networks
    if (isMobile) {
      // Check if network has had time to stabilize - allow bypass after longer timeout
      if (!this.isNetworkStabilized) {
        const timeSinceInit = this.peer && this.peer.open ? Date.now() - (this.peer as any)._createdAt || 0 : 0
        if (timeSinceInit < 15000) { // Allow bypass after 15 seconds regardless
          console.warn('⚠️ Mobile network not yet stabilized, please wait')
          return false
        } else {
          console.log('⚠️ Mobile network stabilization bypassed after 15 seconds')
        }
      }

      // Check current connection load for mobile
      if (this.connections.size >= 5) { // Lower limit for mobile
        console.warn('⚠️ Too many active connections for mobile device')
        return false
      }
    } else {
      // Desktop can handle more connections
      if (this.connections.size >= 10) {
        console.warn('⚠️ Connection limit reached')
        return false
      }
    }

    return true
  }

  // Removed unused method: sendProgressiveHandshake for TypeScript cleanup
}